import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, Mail, Users, Repeat, Crown, X } from "lucide-react";

import type { RootState, AppDispatch } from "../../../redux/store";
import { listenToFoodBrandCustomers } from "../../../redux/foodOrderSlice";
import { fetchAllUsers } from "../../../redux/authSlice";

import {
  HEART, InsightsStyles, StatTile, SectionHead, EmptyState,
  money, compact, fmtDay, initials,
} from "./FreshInsightsKit";

interface FreshCustomersProps { brandId: string }

/* the food slice registers under "FoodOrder"; older stores wired it as
   "foodOrder" — read both so this doesn't depend on the store key */
const foodState = (s: RootState): any =>
  (s as any).FoodOrder ?? (s as any).foodOrder ?? {};

const FreshCustomers: React.FC<FreshCustomersProps> = ({ brandId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const customers = useSelector((s: RootState) => foodState(s).brandCustomers ?? []);
  const allUsers = useSelector((s: RootState) => (s.auth as any).allUsers ?? []);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!brandId) return;
    const unsub = dispatch(listenToFoodBrandCustomers(brandId) as any);
    dispatch(fetchAllUsers());
    return () => { if (typeof unsub === "function") unsub(); };
  }, [brandId, dispatch]);

  /* the customer rows carry only a guestId — names and emails come
     from the users directory */
  const userMap = useMemo(() => {
    const m: Record<string, any> = {};
    (allUsers as any[]).forEach((u) => { m[u.userId ?? u.uid] = u; });
    return m;
  }, [allUsers]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (customers as any[])
      .map((c) => {
        const u = userMap[c.guestId] ?? {};
        const name = [u.firstName, u.lastName].filter(Boolean).join(" ") || u.displayName || "Guest";
        return { ...c, name, email: u.email ?? "", photo: u.profilePicture };
      })
      .filter((c) => !q || `${c.name} ${c.email}`.toLowerCase().includes(q));
  }, [customers, userMap, search]);

  const totals = useMemo(() => {
    const list = customers as any[];
    const spend = list.reduce((s, c) => s + c.totalSpent, 0);
    const repeat = list.filter((c) => c.orders > 1).length;
    return {
      count: list.length,
      spend,
      avg: list.length ? spend / list.length : 0,
      repeatPct: list.length ? Math.round((repeat / list.length) * 100) : 0,
    };
  }, [customers]);

  return (
    <div className="si">
      <InsightsStyles />

      <SectionHead
        title="Diners"
        right={
          <div className="si-search" style={{ maxWidth: 300 }}>
            <div style={{ padding: "0 11px" }}><Search size={13} color={HEART.textFaint} /></div>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email" />
            {search && (
              <button className="si-btn ico sm" style={{ margin: "0 6px" }} onClick={() => setSearch("")} aria-label="Clear search">
                <X size={12} />
              </button>
            )}
          </div>
        }
      />

      <div className="si-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", marginBottom: 20 }}>
        <StatTile icon={<Users size={15} />} label="Diners" value={compact(totals.count)} delay={0} />
        <StatTile icon={<Crown size={15} />} label="Lifetime spend" value={money(totals.spend)} tone="green" delay={1} />
        <StatTile icon={<Crown size={15} />} label="Average per diner" value={money(totals.avg)} delay={2} />
        <StatTile icon={<Repeat size={15} />} label="Ordered more than once" value={`${totals.repeatPct}%`} delay={3} />
      </div>

      {rows.length === 0 ? (
        <EmptyState
          emoji={customers.length === 0 ? "🫙" : "🔍"}
          title={customers.length === 0 ? "No diners yet" : "No one matches"}
          body={customers.length === 0
            ? "Anyone who orders from this kitchen shows up here, ranked by what they've spent."
            : "Try a different name or email."}
        />
      ) : (
        <div className="si-slab">
          <div className="si-strip" />
          <div style={{ padding: "14px 16px 4px" }}>
            <p className="si-eyebrow">RANKED BY LIFETIME SPEND</p>
          </div>

          {rows.map((c, i) => (
            <div key={c.guestId} className="si-row">
              <div className="si-avatar" style={{ overflow: "hidden", padding: 0 }}>
                {c.photo
                  ? <img src={c.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : initials(c.name)}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 7, fontFamily: "'Sora',sans-serif",
                  fontWeight: 800, fontSize: 13.5, letterSpacing: -.2,
                }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                  {i === 0 && (
                    <span className="si-pill" style={{ background: HEART.blush, color: HEART.redDark }}>
                      <Crown size={9} /> Regular
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 10.5, color: HEART.textMuted, fontWeight: 600, marginTop: 3 }}>
                  {c.orders} order{c.orders > 1 ? "s" : ""} · {c.units} dish{c.units === 1 ? "" : "es"} · last {fmtDay(c.lastOrderAt)}
                </div>
              </div>

              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div className="si-num" style={{ fontSize: 15, color: HEART.green }}>{money(c.totalSpent)}</div>
                <span className="si-pill" style={{
                  marginTop: 6,
                  background: c.status === "active" ? HEART.greenSoft : HEART.bg,
                  color: c.status === "active" ? HEART.green : HEART.textMuted,
                }}>
                  {c.status === "active" ? "Active" : "Lapsed"}
                </span>
              </div>

              {c.email && (
                <a className="si-btn ico bl" href={`mailto:${c.email}`} title={`Email ${c.name}`}
                  style={{ textDecoration: "none", flexShrink: 0 }}>
                  <Mail size={14} />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FreshCustomers;
